import React, { useEffect, useRef, useState } from 'react'
import { Header } from './Header'
import { Hero } from './Hero'
import { Benefits } from './Benefits'
import { UseCases } from './UseCases'
import { HowItWorks } from './HowItWorks'
import { SamplePrompts } from './SamplePrompts'
import { AccessControl } from './AccessControl'
import { FinalCTA } from './FinalCTA'
import { Footer } from './Footer'
import { DemoWalkthroughBar } from './DemoWalkthroughBar'
import { WelcomeModal } from './WelcomeModal'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../auth/AuthProvider'
import { authenticateWithBackend } from '../../lib/authenticateWithBackend'

export default function LandingV2App() {
  const [showWelcomeModal, setShowWelcomeModal] = useState(false)
  const [, setHasSeenWelcome] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isSignedIn, loading: isLoaded } = useAuth()
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const autoLoginAttemptedRef = useRef(false)

  function getCookie(name: string): string | null {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop()!.split(';').shift() || null
    return null
  }

  useEffect(() => {
    const seen = localStorage.getItem('aira-welcome-seen')
    if (seen === 'true') {
      setHasSeenWelcome(true)
    }
  }, [])

  const handleGetStartedClick = (event?: React.MouseEvent) => {
    if (event && event.shiftKey) {
      setShowWelcomeModal(true)
      return
    }
    if (event && event.ctrlKey) {
      localStorage.removeItem('aira-welcome-seen')
      setHasSeenWelcome(false)
      setShowWelcomeModal(true)
      return
    }
    navigate('/chat')
  }

  const handleWelcomeClose = () => {
    setShowWelcomeModal(false)
    setHasSeenWelcome(true)
    localStorage.setItem('aira-welcome-seen', 'true')
    navigate('/chat')
  }

  const handleWelcomeGetStarted = () => {
    setHasSeenWelcome(true)
    localStorage.setItem('aira-welcome-seen', 'true')
    navigate('/chat')
  }

  // Show welcome modal when user first signs in
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user) return
    
    // Check if this is a fresh login (not a page reload)
    const seen = localStorage.getItem('aira-welcome-seen')
    const backendAuthed = localStorage.getItem('aira-backend-authed')
    
    // If user just signed in and hasn't seen welcome modal, show it
    if (backendAuthed === 'true' && seen !== 'true') {
      setShowWelcomeModal(true)
    }
  }, [isLoaded, isSignedIn, user])

  // Listen for successful backend authentication
  useEffect(() => {
    const handleAuthCompleted = () => {
      // Backend authentication just completed
      const seen = localStorage.getItem('aira-welcome-seen')
      if (seen !== 'true' && isSignedIn && user) {
        setShowWelcomeModal(true)
      }
    }

    window.addEventListener('aira-auth-completed', handleAuthCompleted)
    return () => window.removeEventListener('aira-auth-completed', handleAuthCompleted)
  }, [isSignedIn, user])

  // Autologin to backend when user is signed in (but don't auto-redirect)
  useEffect(() => {
    const run = async () => {
      if (!isLoaded || !isSignedIn || !user) return
      // Prevent repeated attempts within a session or when already authed
      if (autoLoginAttemptedRef.current) return
      
      // If already authenticated, just show welcome modal if needed (no redirect)
      if (getCookie('aira_status') === 'true' && localStorage.getItem('aira-backend-authed') === 'true') {
        const seen = localStorage.getItem('aira-welcome-seen')
        if (seen !== 'true') {
          setShowWelcomeModal(true)
        }
        return
      }
      
      if (localStorage.getItem('aira-backend-authed') === 'true') {
        // Backend already authenticated, just show welcome modal if needed (no redirect)
        const seen = localStorage.getItem('aira-welcome-seen')
        if (seen !== 'true') {
          setShowWelcomeModal(true)
        }
        return
      }
      
      if (isAuthenticating) return
      setIsAuthenticating(true)
      autoLoginAttemptedRef.current = true
      try {
        if (user.email) {
          const res = await authenticateWithBackend(user)
          if (res.ok) {
            localStorage.setItem('aira-backend-authed', 'true')
            // Dispatch custom event to notify about successful auth
            window.dispatchEvent(new CustomEvent('aira-auth-completed'))
            // Show welcome modal after successful authentication if not seen before (no redirect)
            const seen = localStorage.getItem('aira-welcome-seen')
            if (seen !== 'true') {
              setShowWelcomeModal(true)
            }
          }
        }
      } finally {
        setIsAuthenticating(false)
      }
    }
    run()
  }, [isLoaded, isSignedIn, user, isAuthenticating, navigate])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header onGetStartedClick={handleGetStartedClick} />
      <main>
        <Hero />
        <Benefits />
        <UseCases />
        <HowItWorks />
        <SamplePrompts />
        <AccessControl />
        <FinalCTA onGetStartedClick={handleGetStartedClick} />
      </main>
      <Footer />
      <DemoWalkthroughBar />
      <WelcomeModal isOpen={showWelcomeModal} onClose={handleWelcomeClose} onGetStarted={handleWelcomeGetStarted} />
    </div>
  )
}


